import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FaFilePdf, FaCode, FaSave, FaLock, FaCheckCircle, FaArrowRight, FaExclamationTriangle, FaPlus, FaTimes } from 'react-icons/fa';

/**
 * ProductDescriptionViewer
 *
 * GEO-optimierte Produktseiten-Vorschau MIT Inline-Editing (Muster wie
 * BlogArticleViewer): jeder der 9 Blöcke ist direkt editierbar, Arrays
 * (bullets, use_cases, qa, specs) mit +/−. Preis ist ein strukturiertes
 * Feld (Zahl + price_visibility), nie Prosa.
 *
 * Erfindet NICHTS: leere Blöcke bleiben im Export/Markup weg (has()-Guards
 * in buildHtml/buildJsonLd). Der Editor zeigt leere Felder mit Platzhalter,
 * damit der Kunde selbst befüllen kann — das ist kein Erfinden.
 *
 * ref: { save(), getData(), getCleanHtml() } — der Expanded-Modal-Save
 * delegiert hierhin (analog BlogArticleViewer).
 *
 * Props:
 *   data, factWarnings[], heroImageUrl, language, brandName, contentId,
 *   articleKey (erzwingt Re-Sync), onSave(updated, cleanHtml), getAuthHeader
 */

const has = (v) => v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0);

// Roh-Daten in ein editierbares, formstabiles Objekt normalisieren.
const normalize = (d = {}) => ({
  headline: d.headline || '',
  short_description: d.short_description || '',
  subheadline: d.subheadline || '',
  bullets: Array.isArray(d.bullets) ? d.bullets.slice() : [],
  long_description: d.long_description || '',
  use_cases: Array.isArray(d.use_cases) ? d.use_cases.slice() : [],
  qa: Array.isArray(d.qa) ? d.qa.map(q => ({ question: q?.question || '', answer: q?.answer || '' })) : [],
  specs: Array.isArray(d.specs) ? d.specs.map(s => ({ label: s?.label || '', value: s?.value || '' })) : [],
  price: (d.price === 0 || d.price) ? d.price : '',
  price_visibility: d.price_visibility || '',
  price_currency: d.price_currency || '',
});

// ── Unkontrolliertes contentEditable-Primitiv ──────────────────────────────
// React + contentEditable + children = Duplikat-Text-Bug. Wir verwalten den
// Textinhalt imperativ und committen erst onBlur (Muster aus BlogArticleViewer).
function EditableText({ value, onCommit, className = '', placeholder = '', multiline = false, style }) {
  const ref = useRef(null);
  const last = useRef(value ?? '');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value ?? '';
    if (document.activeElement !== el && el.innerText !== incoming) {
      el.innerText = incoming;
    }
    last.current = incoming;
  }, [value]);
  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      className={`pd-editable ${className}`}
      style={style}
      onBlur={(e) => {
        const text = e.currentTarget.innerText.replace(/ /g, ' ').replace(/\n{3,}/g, '\n\n').trimEnd();
        if (text !== last.current) { last.current = text; onCommit(text); }
      }}
      onKeyDown={(e) => {
        if (!multiline && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
      }}
    />
  );
}

// Kleiner Lösch-Button für Array-Einträge
function DelBtn({ onClick, title = 'Eintrag löschen' }) {
  return (
    <button onClick={onClick} title={title}
      className="flex-shrink-0 text-[#B4B2A9] hover:text-[#C0392B] transition-colors mt-1"
      type="button">
      <FaTimes className="text-xs" />
    </button>
  );
}

// „+ hinzufügen"-Button
function AddBtn({ onClick, label }) {
  return (
    <button onClick={onClick} type="button"
      className="inline-flex items-center gap-1.5 text-[12px] text-[#0F6E56] hover:text-[#0a4a3a] mt-1">
      <FaPlus className="text-[10px]" /> {label}
    </button>
  );
}

const ProductDescriptionViewer = forwardRef(function ProductDescriptionViewer({
  data = {},
  factWarnings = [],
  heroImageUrl = '',
  language = 'de',
  brandName = '',
  contentId,
  articleKey,
  onSave,
  getAuthHeader,
}, ref) {
  const [tab, setTab] = useState('preview'); // 'preview' | 'html'
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(() => normalize(data));

  // Re-Sync nur, wenn sich das zugrundeliegende Content-Piece ändert —
  // NICHT bei jedem Parent-Re-Render (würde Inline-Edits überschreiben).
  const syncKey = articleKey != null ? String(articleKey) : `id:${contentId || ''}`;
  const lastKey = useRef(syncKey);
  useEffect(() => {
    if (syncKey !== lastKey.current) {
      lastKey.current = syncKey;
      setContent(normalize(data));
    }
  }, [syncKey, data]);

  const update = (key, value) => setContent(prev => ({ ...prev, [key]: value }));

  const {
    headline, short_description, subheadline, bullets, long_description,
    use_cases, qa, specs,
  } = content;

  // ── Preis: Zahl → { currency, value } (nur ableiten, nie erfinden) ──
  const parsePrice = (raw) => {
    const s = String(raw || '');
    const currency = /€|eur/i.test(s) ? 'EUR' : /\$|usd/i.test(s) ? 'USD' : /£|gbp/i.test(s) ? 'GBP' : '';
    const t = s.replace(/[^\d.,]/g, '');
    const lastDot = t.lastIndexOf('.');
    const lastComma = t.lastIndexOf(',');
    const decPos = Math.max(lastDot, lastComma);
    let value;
    if (decPos === -1) {
      value = parseFloat(t);
    } else {
      const hasBoth = lastDot !== -1 && lastComma !== -1;
      const decimals = t.length - decPos - 1;
      if (hasBoth || decimals <= 2) {
        const intPart = t.slice(0, decPos).replace(/[.,]/g, '');
        value = parseFloat(intPart + '.' + t.slice(decPos + 1));
      } else {
        value = parseFloat(t.replace(/[.,]/g, ''));
      }
    }
    return { currency, value: Number.isFinite(value) ? value : null };
  };

  // ── Preis strikt strukturiert: Zahl im Feld, Darstellung über price_visibility ──
  const PRICE_LABEL_RE = /^\s*(preis|price)\b/i;
  const VISIBILITIES = ['range', 'exact', 'on_request']; // hidden entfällt: keine Zahl = kein Preis
  const priceVisibility = VISIBILITIES.includes(content.price_visibility) ? content.price_visibility : 'range';
  const CUR_SYMBOL = { EUR: '€', USD: '$', GBP: '£' };
  const curSymbol = (c) => CUR_SYMBOL[c] || c || '€';
  const resolvePrice = () => {
    let value = null;
    let currency = content.price_currency || 'EUR';
    if (content.price != null && content.price !== '' && Number.isFinite(Number(content.price))) {
      value = Number(content.price);
    } else {
      const ps = specs.find(s => PRICE_LABEL_RE.test(s.label || ''));
      if (ps && has(ps.value)) {
        const p = parsePrice(ps.value);
        if (p.value != null) { value = p.value; if (p.currency) currency = p.currency; }
      }
    }
    if (value == null) return null;
    return { value, currency, visibility: priceVisibility };
  };
  const priceInfo = resolvePrice();
  // JSON-LD/offers nur bei sichtbarem Preis (exact/range), nie bei on_request
  const priceForMarkup = priceInfo && (priceInfo.visibility === 'exact' || priceInfo.visibility === 'range') ? priceInfo : null;
  // sichtbarer Preis-Text, deterministisch. Keine Zahl → keine Preiszeile.
  const priceLabel = !priceInfo ? ''
    : priceInfo.visibility === 'on_request' ? 'Auf Anfrage'
    : (priceInfo.visibility === 'range' ? 'ab ' : '') + priceInfo.value.toLocaleString('de-DE') + ' ' + curSymbol(priceInfo.currency);
  const nonPriceSpecs = specs.filter(s => !PRICE_LABEL_RE.test(s.label || ''));

  // ── JSON-LD (Product) — jedes Feld nur aus geliefertem Fakt/Brand ──
  const buildJsonLd = () => {
    const brandSpec = specs.find(s => /marke|brand|anbieter|hersteller/i.test(s.label || ''));
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: headline || '',
      description: short_description || '',
    };
    const brand = (brandName && brandName.trim()) || (brandSpec && brandSpec.value) || '';
    if (brand) ld.brand = { '@type': 'Organization', name: brand };
    if (priceForMarkup) {
      ld.offers = {
        '@type': 'Offer',
        price: priceForMarkup.value,
        priceCurrency: priceForMarkup.currency,
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: priceForMarkup.value,
          priceCurrency: priceForMarkup.currency,
        },
      };
    }
    const propSpecs = nonPriceSpecs.filter(s => s !== brandSpec && has(s.label) && has(s.value));
    if (propSpecs.length) {
      ld.additionalProperty = propSpecs.map(s => ({ '@type': 'PropertyValue', name: s.label, value: s.value }));
    }
    return JSON.stringify(ld, null, 2);
  };

  // ── HTML-Export: sichtbare Blöcke + JSON-LD (leere Blöcke bleiben weg) ──
  const buildHtml = () => {
    const esc = (s) => String(s || '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const parts = [];
    parts.push(`<article>`);
    if (has(headline)) parts.push(`<h1>${esc(headline)}</h1>`);
    if (has(short_description)) parts.push(`<p class="lead">${esc(short_description)}</p>`);
    if (has(subheadline)) parts.push(`<p class="sub">${esc(subheadline)}</p>`);
    if (has(bullets)) parts.push(`<ul>${bullets.filter(has).map(b => `<li>${esc(b)}</li>`).join('')}</ul>`);
    if (has(long_description)) parts.push(`<div class="body">${esc(long_description).replace(/\n/g, '<br>')}</div>`);
    if (has(use_cases)) parts.push(`<h2>Anwendungsfälle</h2><ul>${use_cases.filter(has).map(u => `<li>${esc(u)}</li>`).join('')}</ul>`);
    if (has(qa)) parts.push(`<h2>Häufige Fragen</h2>${qa.filter(q => has(q.question) || has(q.answer)).map(q => `<div class="qa"><h3>${esc(q.question)}</h3><p>${esc(q.answer)}</p></div>`).join('')}`);
    if (priceLabel) parts.push(`<p class="price"><strong>Preis:</strong> ${esc(priceLabel)}</p>`);
    if (has(nonPriceSpecs)) parts.push(`<h2>Details</h2><dl>${nonPriceSpecs.filter(s => has(s.label) || has(s.value)).map(s => `<dt>${esc(s.label)}</dt><dd>${esc(s.value)}</dd>`).join('')}</dl>`);
    parts.push(`</article>`);
    parts.push(`<script type="application/ld+json">${buildJsonLd()}</script>`);
    return parts.join('\n');
  };

  const handleExportHtml = () => {
    const blob = new Blob([buildHtml()], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `produktbeschreibung-${contentId || 'export'}.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Self-contained druckbares HTML-Dokument (Muster: BlogArticleViewer.buildPrintableDocument).
  const buildPrintableDocument = () => {
    const escAttr = (s) => String(s || '').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const docTitle = headline || 'Produktbeschreibung';
    const hero = heroImageUrl ? `<img class="hero" src="${escAttr(heroImageUrl)}" alt="">` : '';
    const css = `
      @page { size: A4; margin: 1.8cm 2cm; }
      * { box-sizing: border-box; }
      html, body { background: #ffffff; margin: 0; padding: 0; }
      body { font-family: 'Plus Jakarta Sans','Helvetica Neue',Arial,sans-serif; color: #0a1628;
             line-height: 1.65; font-size: 11pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      article { max-width: 760px; margin: 0 auto; padding: 12px 0; }
      .hero { display: block; width: 100%; max-width: 760px; margin: 0 auto 20px; border-radius: 8px; }
      h1 { font-size: 24pt; line-height: 1.2; margin: 0 0 12px; font-weight: 600; }
      h2 { font-size: 14pt; margin: 24px 0 10px; font-weight: 600; page-break-after: avoid; }
      h3 { font-size: 12pt; margin: 14px 0 4px; font-weight: 600; }
      p { margin: 0 0 10px; } p.lead { font-size: 13pt; font-weight: 500; }
      p.sub { color: #5F5E5A; border-left: 3px solid #1e9ead; padding-left: 12px; }
      ul { padding-left: 20px; margin: 10px 0; } li { margin: 4px 0; }
      .qa { page-break-inside: avoid; margin-bottom: 12px; }
      p.price { font-size: 12pt; margin: 16px 0; }
      dl { display: grid; grid-template-columns: max-content 1fr; gap: 6px 18px; margin: 8px 0; }
      dt { color: #5F5E5A; } dd { margin: 0; }
      @media screen {
        body { padding: 24px; background: #f5f5f5; }
        article { background: #fff; padding: 40px; border-radius: 6px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); }
      }
    `;
    // Auto-Print im Kontext des neuen Tabs — zuverlässiger als das load-Event
    // vom Opener (das bei Blob-URLs oft schon vorbei ist, bevor der Listener greift).
    const autoPrint = `<script>window.onload=function(){setTimeout(function(){try{window.print();}catch(e){}},300);};<\/script>`;
    const bodyHtml = buildHtml().replace('<article>', `<article>${hero}`);
    return `<!DOCTYPE html><html lang="${escAttr(language || 'de')}"><head><meta charset="utf-8">`
      + `<meta name="viewport" content="width=device-width, initial-scale=1"><title>${escAttr(docTitle)}</title>`
      + `<style>${css}</style></head><body>${bodyHtml}${autoPrint}</body></html>`;
  };

  const handleExportPdf = () => {
    const docHtml = buildPrintableDocument();
    const blob = new Blob([docHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) {
      // Popup geblockt → HTML als Download-Fallback
      const a = document.createElement('a');
      a.href = url; a.download = `produktbeschreibung-${contentId || 'export'}.html`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else {
      const tryPrint = () => { try { w.focus(); w.print(); } catch (_) {} };
      try { w.addEventListener('load', () => setTimeout(tryPrint, 200)); } catch (_) {}
      setTimeout(tryPrint, 1500);
    }
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try { await onSave(content, buildHtml()); } finally { setSaving(false); }
  };

  // ref-API für den Expanded-Modal-Save (analog BlogArticleViewer)
  useImperativeHandle(ref, () => ({
    save: handleSave,
    getData: () => content,
    getCleanHtml: () => buildHtml(),
  }), [content, onSave]);

  // ── Array-Mutatoren ──
  const setArr = (key, idx, val) => update(key, content[key].map((x, i) => (i === idx ? val : x)));
  const delArr = (key, idx) => update(key, content[key].filter((_, i) => i !== idx));
  const addArr = (key, empty) => update(key, [...content[key], empty]);

  const placeholderStyle = (
    <style>{`
      .pd-editable:empty::before { content: attr(data-placeholder); color: #B4B2A9; pointer-events: none; }
      .pd-editable:focus { outline: 2px solid #9FE1CB; outline-offset: 2px; border-radius: 4px; }
      .pd-editable { white-space: pre-wrap; }
    `}</style>
  );

  // ── HTML-Tab: rohes Markup anzeigen ──
  if (tab === 'html') {
    return (
      <div className="text-white">
        <TabBar tab={tab} setTab={setTab} onPdf={handleExportPdf} onHtml={handleExportHtml} onSave={handleSave} saving={saving} />
        <pre className="bg-[#0d1b2e] border border-[#1e3a52] rounded-xl p-4 text-xs text-[#9FE1CB] overflow-auto max-h-[70vh] whitespace-pre-wrap">
          {buildHtml()}
        </pre>
      </div>
    );
  }

  // ── Preview-Tab: breites Produktseiten-Layout (inline editierbar) ──
  return (
    <div className="text-white">
      {placeholderStyle}
      <TabBar tab={tab} setTab={setTab} onPdf={handleExportPdf} onHtml={handleExportHtml} onSave={handleSave} saving={saving} />

      {factWarnings.length > 0 && (
        <div className="mb-4 flex items-start gap-2 bg-[#3a2a12] border border-[#8a6d3b] rounded-lg px-4 py-3">
          <FaExclamationTriangle className="text-[#f0ad4e] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#f0d9a8]">
            <span className="font-medium">Hinweis: </span>
            {factWarnings.join(', ')}. Bitte prüfen — nicht ungeprüft veröffentlichen.
          </div>
        </div>
      )}

      {/* Weiße Produktseiten-Karte */}
      <div className="bg-white rounded-2xl px-8 py-8 text-[#0a1628]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>

        {/* Badges */}
        <div className="flex justify-between items-start gap-4 mb-2 flex-wrap">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 bg-[#E1F5EE] text-[#0F6E56] text-xs font-medium px-3 py-1.5 rounded-md">GEO-optimiert</span>
          </div>
        </div>

        {/* H1 */}
        <EditableText
          value={headline}
          onCommit={(v) => update('headline', v)}
          placeholder="Headline: Name + Hauptnutzen"
          className="text-[30px] leading-tight font-medium text-[#0a1628] mt-1.5 mb-5 max-w-4xl"
        />

        {/* Oberer 2-Spalten-Bereich: Bild | Kurzbeschreibung + Fakten */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7 items-start mb-7">
          {/* Links: Bild */}
          <div>
            {heroImageUrl ? (
              <img src={heroImageUrl} alt={headline} className="w-full rounded-xl object-cover" style={{ aspectRatio: '4/3' }} />
            ) : (
              <div className="bg-[#F1EFE8] rounded-xl flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
                <span className="text-[#B4B2A9] text-sm">Kein Bild</span>
              </div>
            )}
          </div>

          {/* Rechts: Kurzbeschreibung (VOR subheadline) + Fakten-Panel */}
          <div>
            <EditableText
              value={short_description}
              onCommit={(v) => update('short_description', v)}
              placeholder="Kurzbeschreibung: 1 zitierbarer Satz"
              multiline
              className="text-[17px] leading-relaxed text-[#0a1628] font-medium mb-4"
            />

            {/* Fakten-Panel — editierbar */}
            <div className="border-t border-[#E5E5E0] pt-3">
              <div className="text-[11px] tracking-wider text-[#888780] uppercase mb-2 flex items-center gap-1.5">
                <FaLock className="text-[10px]" /> Fakten
              </div>

              {/* Preis: Zahl + Darstellungs-Dropdown (kein Freitext) */}
              <div className="flex items-center gap-2 py-1.5 border-b border-[#F1EFE8]">
                <span className="text-sm text-[#5F5E5A] flex-shrink-0">Preis</span>
                <input
                  type="number"
                  inputMode="decimal"
                  value={content.price === '' || content.price == null ? '' : content.price}
                  onChange={(e) => update('price', e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="—"
                  className="w-24 ml-auto text-sm text-right text-[#0a1628] bg-[#F8F8F5] border border-[#E5E5E0] rounded px-2 py-1 focus:outline-none focus:border-[#1e9ead]"
                />
                <select
                  value={priceVisibility}
                  onChange={(e) => update('price_visibility', e.target.value)}
                  className="text-sm text-[#0a1628] bg-[#F8F8F5] border border-[#E5E5E0] rounded px-2 py-1 focus:outline-none focus:border-[#1e9ead]"
                  title="Wie der Preis dargestellt wird"
                >
                  <option value="range">ab Preis</option>
                  <option value="exact">Festpreis</option>
                  <option value="on_request">Auf Anfrage</option>
                </select>
              </div>
              {priceLabel && (
                <div className="text-[11px] text-[#888780] py-1">→ zeigt: <span className="text-[#0a1628] font-medium">{priceLabel}</span></div>
              )}

              {/* Specs (ohne Preis) — editierbar mit +/− */}
              {nonPriceSpecs.map((s) => {
                const gi = specs.indexOf(s); // Index im vollen specs-Array
                return (
                  <div key={gi} className="flex items-start gap-2 py-1.5 border-b border-[#F1EFE8]">
                    <EditableText
                      value={s.label}
                      onCommit={(v) => setArr('specs', gi, { ...specs[gi], label: v })}
                      placeholder="Label"
                      className="text-sm text-[#5F5E5A] flex-1 min-w-0"
                    />
                    <EditableText
                      value={s.value}
                      onCommit={(v) => setArr('specs', gi, { ...specs[gi], value: v })}
                      placeholder="Wert"
                      className="text-sm text-[#0a1628] text-right flex-1 min-w-0"
                    />
                    <DelBtn onClick={() => delArr('specs', gi)} />
                  </div>
                );
              })}
              <AddBtn onClick={() => addArr('specs', { label: '', value: '' })} label="Fakt hinzufügen" />

              <div className="text-[11px] text-[#888780] mt-2 flex items-center gap-1.5">
                <FaLock className="text-[10px]" /> Preis ist ein Feld · Darstellung über Sichtbarkeit · geht ins JSON-LD
              </div>
            </div>
          </div>
        </div>

        {/* Subheadline / Abgrenzung */}
        <div className="border-l-[3px] border-[#1e9ead] pl-4 mb-7 max-w-3xl">
          <EditableText
            value={subheadline}
            onCommit={(v) => update('subheadline', v)}
            placeholder="Subheadline: für wen / Abgrenzung"
            multiline
            className="text-[15px] leading-relaxed text-[#5F5E5A]"
          />
        </div>

        {/* Unterer 2-Spalten-Bereich: Langtext | Nutzen-Bullets */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* Links: Langbeschreibung + Abgrenzungsblock */}
          <div>
            <div className="text-[11px] tracking-wider text-[#888780] uppercase mb-2">Beschreibung</div>
            <EditableText
              value={long_description}
              onCommit={(v) => update('long_description', v)}
              placeholder="Langbeschreibung (kurze Sätze, nur Fakten)"
              multiline
              className="text-[15px] leading-relaxed text-[#0a1628] mb-4"
            />
          </div>

          {/* Rechts: Nutzen-Bullets + Anwendungsfälle */}
          <div>
            <div className="text-[11px] tracking-wider text-[#888780] uppercase mb-3">Das bekommt ihr</div>
            <div className="flex flex-col gap-3 mb-2">
              {bullets.map((b, i) => (
                <div key={i} className="flex gap-2.5 items-start">
                  <FaCheckCircle className="text-[#1e9ead] text-lg flex-shrink-0 mt-0.5" />
                  <EditableText
                    value={b}
                    onCommit={(v) => setArr('bullets', i, v)}
                    placeholder="Nutzen-Aussage"
                    className="text-sm leading-snug text-[#0a1628] flex-1 min-w-0"
                  />
                  <DelBtn onClick={() => delArr('bullets', i)} />
                </div>
              ))}
            </div>
            <AddBtn onClick={() => addArr('bullets', '')} label="Bullet hinzufügen" />

            <div className="text-[11px] tracking-wider text-[#888780] uppercase mb-3 mt-6">Wofür es sich eignet</div>
            <div className="flex flex-col gap-2 mb-2">
              {use_cases.map((u, i) => (
                <div key={i} className="flex gap-2.5 items-start">
                  <FaArrowRight className="text-[#1e9ead] text-sm flex-shrink-0 mt-1" />
                  <EditableText
                    value={u}
                    onCommit={(v) => setArr('use_cases', i, v)}
                    placeholder="Anwendungsfall (wie echte Nutzerfrage)"
                    className="text-sm leading-snug text-[#0a1628] flex-1 min-w-0"
                  />
                  <DelBtn onClick={() => delArr('use_cases', i)} />
                </div>
              ))}
            </div>
            <AddBtn onClick={() => addArr('use_cases', '')} label="Anwendungsfall hinzufügen" />
          </div>
        </div>

        {/* Q&A — editierbar */}
        <div className="mt-7 pt-5 border-t border-[#E5E5E0]">
          <h2 className="text-[19px] font-medium text-[#0a1628] mb-4">Häufige Fragen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {qa.map((q, i) => (
              <div key={i} className="relative pr-5">
                <EditableText
                  value={q.question}
                  onCommit={(v) => setArr('qa', i, { ...qa[i], question: v })}
                  placeholder="Frage"
                  className="text-sm font-medium text-[#0a1628] mb-1"
                />
                <EditableText
                  value={q.answer}
                  onCommit={(v) => setArr('qa', i, { ...qa[i], answer: v })}
                  placeholder="Antwort (30–60 Wörter)"
                  multiline
                  className="text-sm leading-relaxed text-[#5F5E5A]"
                />
                <div className="absolute top-0 right-0"><DelBtn onClick={() => delArr('qa', i)} /></div>
              </div>
            ))}
          </div>
          <AddBtn onClick={() => addArr('qa', { question: '', answer: '' })} label="Frage hinzufügen" />
        </div>

        {/* Markup-Fußzeile */}
        <div className="mt-6 pt-4 border-t border-[#E5E5E0] flex items-center gap-2">
          <FaCode className="text-[#888780] text-sm" />
          <span className="text-[13px] text-[#888780]">Product-Markup (JSON-LD) enthalten · Q&A als sichtbarer Text · Preis nur im Markup</span>
        </div>
      </div>
    </div>
  );
});

function TabBar({ tab, setTab, onPdf, onHtml, onSave, saving }) {
  return (
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      <div className="flex bg-[#12233d] rounded-lg p-0.5">
        <button onClick={() => setTab('preview')}
          className={`text-[13px] px-3.5 py-1.5 rounded-md ${tab === 'preview' ? 'bg-[#1e9ead] text-[#04252b] font-medium' : 'text-[#94a3b8]'}`}>
          Vorschau
        </button>
        <button onClick={() => setTab('html')}
          className={`text-[13px] px-3.5 py-1.5 rounded-md ${tab === 'html' ? 'bg-[#1e9ead] text-[#04252b] font-medium' : 'text-[#94a3b8]'}`}>
          HTML
        </button>
      </div>
      <div className="ml-auto flex gap-2">
        <button onClick={onSave} disabled={saving}
          className="inline-flex items-center gap-1.5 bg-[#1e9ead] text-[#04252b] text-[13px] font-medium px-3 py-1.5 rounded-md disabled:opacity-50">
          <FaSave className="text-[13px]" />{saving ? 'Speichert…' : 'Speichern'}
        </button>
        <button onClick={onPdf}
          className="inline-flex items-center gap-1.5 border border-[#2d4159] text-[#e2e8f0] text-[13px] px-3 py-1.5 rounded-md">
          <FaFilePdf className="text-[13px]" />PDF
        </button>
        <button onClick={onHtml}
          className="inline-flex items-center gap-1.5 border border-[#2d4159] text-[#e2e8f0] text-[13px] px-3 py-1.5 rounded-md">
          <FaCode className="text-[13px]" />HTML
        </button>
      </div>
    </div>
  );
}

export default ProductDescriptionViewer;
